// ============================================================================
// ServerUnreachableScreen: what a page load shows while QuoteMaker's server is
// not answering (outage lane, 2026-09-29).
// ----------------------------------------------------------------------------
// Customer Zero refreshed during a backend deploy and was dropped on the login
// form, whose next attempt said "Invalid email or password". The session was
// fine the whole time. This page holds the session instead: one calm sentence,
// the fact that the work is kept, a countdown to the next automatic try and a
// Try now button. It never offers the login form, because nothing here means
// the operator is signed out.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { retryCountdownText, secondsUntil } from '../../utils/retry-countdown';
import '../../styles/server-status.css';

export interface ServerUnreachableScreenProps {
    /** Epoch ms of the next automatic try, or null while a try is running. */
    nextRetryAt: number | null;
    onTryNow: () => void;
}

export function ServerUnreachableScreen({ nextRetryAt, onTryNow }: ServerUnreachableScreenProps) {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        setNow(Date.now());
        const timer = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, [nextRetryAt]);

    const countdown = retryCountdownText(secondsUntil(nextRetryAt, now));

    return (
        <main className="qm-server-status" data-qm-surface="server-unreachable">
            <section className="qm-server-status__panel" aria-labelledby="qm-server-status-title">
                <p className="qm-server-status__brand">QuoteMaker</p>
                <h1 id="qm-server-status-title" className="qm-server-status__title">
                    QuoteMaker is updating or can&apos;t reach its server.
                </h1>
                <p className="qm-server-status__body">
                    Your work is kept on this device. We&apos;ll reconnect by ourselves.
                </p>
                <p className="qm-server-status__countdown" role="status" aria-live="polite">
                    <span className="qm-server-status__pulse" aria-hidden="true" />
                    {countdown}
                </p>
                <button type="button" className="qm-server-status__try" onClick={onTryNow}>
                    Try now
                </button>
                <p className="qm-server-status__note">
                    If this lasts more than a few minutes, tell your administrator.
                </p>
            </section>
        </main>
    );
}

export default ServerUnreachableScreen;
